import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import type {
  ChannelSummary,
  SendbirdClientLike,
  SendbirdConnectionState,
  SendbirdGroupChannel,
  SendbirdUser
} from '@synergy-chat/sendbird-shared';
import {
  connectToSendbird,
  disconnectFromSendbird,
  fetchChannelsUnread,
  unregisterGroupChannelHandler
} from '@synergy-chat/sendbird-shared';

import { renderBell } from './templates/notifier-ui';
import tailwindStyles from './styles/tailwind-component.css?inline';

/**
 * SynergyChatNotifier Web Component
 *
 * A lightweight bell-icon notification component that monitors one or more
 * Sendbird group channels for unread messages. Emits custom events so that
 * the parent application can handle browser notifications, toasts, etc.
 *
 * Initialization (JS properties — set before calling connect()):
 *   getInitializationParams  — async () => { appId, userId, channelIds }
 *   initializeSession        — async () => { authToken? }  (scaffold — not yet wired)
 *   refreshSession           — async () => { authToken? }  (scaffold — not yet wired)
 *
 * Methods:
 *   connect()  — call after setting getInitializationParams to connect to Sendbird
 *
 * Events (bubbles + composed):
 *   notifier-ready         — { userId, channelUrls }
 *   notifier-error         — { error: string }
 *   notify-message         — { channelUrl, channelName, senderId, senderName,
 *                              messageText, channelUnreadCount, totalUnreadCount }
 *   unread-counts-changed  — { totalUnreadCount, channels: ChannelSummary[] }
 *   bell-clicked           — { totalUnreadCount, channels: ChannelSummary[] }
 */
@customElement('synergy-chat-notifier')
export class SynergyChatNotifier extends LitElement {
  // ── Public JS properties (set by parent before calling connect()) ──────────

  /** Async function that returns Sendbird connection parameters. Must be set before connect(). */
  getInitializationParams?: () => Promise<{ appId: string; userId: string; channelIds: string }>;

  /**
   * Scaffold: will be called to obtain the Sendbird auth token before connecting.
   * Not yet wired — reserved for a future session-handling phase.
   */
  initializeSession?: () => Promise<{ authToken?: string }>;

  /**
   * Scaffold: will be called when the Sendbird session needs refreshing.
   * Not yet wired — reserved for a future session-handling phase.
   */
  refreshSession?: () => Promise<{ authToken?: string }>;

  // ── Internal reactive state ───────────────────────────────────────────────

  @state() private _unreadMap: Map<string, ChannelSummary> = new Map();
  @state() private _connectionState: SendbirdConnectionState = 'CLOSED';

  // ── Private instance fields ───────────────────────────────────────────────

  private _appId = '';
  private _userId = '';
  private _authToken = '';
  private _channelIds = '';
  private _client: SendbirdClientLike | null = null;
  private _currentUser: SendbirdUser | null = null;
  private _channelHandlerId: string | null = null;
  /** Channel URLs currently registered with the handler */
  private _registeredChannelUrls: string[] = [];

  // ── Styles ────────────────────────────────────────────────────────────────

  static styles = [
    unsafeCSS(tailwindStyles),
    css`
      :host {
        /* CSS custom properties — override these on the host element */
        --scn-primary-color: #3b82f6;
        --scn-badge-bg: #ef4444;
        --scn-badge-text-color: #ffffff;
        --scn-font-family: 'Inter', ui-sans-serif, system-ui, -apple-system,
          'Segoe UI', Roboto, sans-serif;
        --scn-host-z-index-open: 999;
        --scn-panel-z-index: 1000;

        /* Host layout — inline-block so the panel anchors relative to the bell */
        display: inline-block;
        position: relative;
        z-index: 0;
        font-family: var(--scn-font-family);
        box-sizing: border-box;
      }

      /* Invisible root wrapper — fills host */
      .notifier-root {
        display: contents;
      }

      /* ── Bell button ── */
      .bell-button {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        padding: 0;
        border: none;
        border-radius: 9999px;
        background: transparent;
        color: var(--scn-primary-color);
        cursor: pointer;
        transition: background-color 0.15s ease, color 0.15s ease;
        outline: none;
      }

      .bell-button:hover {
        background-color: color-mix(in srgb, var(--scn-primary-color) 12%, transparent);
      }

      .bell-button[aria-expanded="true"] {
        background-color: color-mix(in srgb, var(--scn-primary-color) 18%, transparent);
        color: var(--scn-primary-color);
      }

      .bell-button:focus-visible {
        outline: 2px solid var(--scn-primary-color);
        outline-offset: 2px;
      }

      .bell-icon {
        width: 1.5rem;
        height: 1.5rem;
        pointer-events: none;
      }

      /* ── Unread badge ── */
      .badge {
        position: absolute;
        top: 0.1rem;
        right: 0.1rem;
        min-width: 1.1rem;
        height: 1.1rem;
        padding: 0 0.3rem;
        border-radius: 9999px;
        background: var(--scn-badge-bg);
        color: var(--scn-badge-text-color);
        font-size: 0.65rem;
        font-weight: 700;
        line-height: 1.1rem;
        text-align: center;
        pointer-events: none;
        white-space: nowrap;
      }
    `
  ];

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override connectedCallback(): void {
    super.connectedCallback();
    // Connection is initiated explicitly by the parent via connect().
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._teardown();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  override render() {
    const totalUnread = this._totalUnread();
    return html`
      <div class="notifier-root">
        ${renderBell({
          totalUnread,
          onBellClick: () => this._handleBellClick()
        })}
      </div>
    `;
  }

  // ── Sendbird connect / teardown ───────────────────────────────────────────

  /**
   * Initializes the component. Call this after setting getInitializationParams.
   * Awaits getInitializationParams(), then connects to Sendbird and seeds unread counts.
   * Safe to call again to reconnect with fresh parameters.
   */
  async connect(): Promise<void> {
    if (!this.getInitializationParams) {
      this._dispatch('notifier-error', {
        error: 'getInitializationParams must be set before calling connect().'
      });
      return;
    }
    try {
      const params = await this.getInitializationParams();
      this._appId      = params.appId      ?? '';
      this._userId     = params.userId     ?? '';
      this._channelIds = params.channelIds ?? '';
      this._authToken  = '';
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this._dispatch('notifier-error', {
        error: `getInitializationParams failed: ${message}`
      });
      return;
    }
    await this._connect();
  }

  private async _connect(): Promise<void> {
    if (!this._appId || !this._userId) {
      this._dispatch('notifier-error', { error: 'appId and userId are required.' });
      return;
    }

    this._connectionState = 'CONNECTING';

    try {
      const { client, currentUser } = await connectToSendbird({
        appId: this._appId,
        userId: this._userId,
        authToken: this._authToken || undefined
      });

      this._client = client;
      this._currentUser = currentUser;
      this._connectionState = 'OPEN';

      const channelUrls = this._parseChannelIds();
      await this._seedUnreadMap(channelUrls);
      await this._registerHandler(channelUrls);

      this._dispatch('notifier-ready', {
        userId: currentUser.userId,
        channelUrls
      });
    } catch (error) {
      this._connectionState = 'CLOSED';
      const message = error instanceof Error ? error.message : String(error);
      console.error('[SynergyChatNotifier] Connection failed:', error);
      this._dispatch('notifier-error', { error: message });
    }
  }

  private async _teardown(): Promise<void> {
    if (this._channelHandlerId && this._client) {
      unregisterGroupChannelHandler(this._client, this._channelHandlerId);
      this._channelHandlerId = null;
    }
    if (this._client) {
      await disconnectFromSendbird(this._client);
      this._client = null;
    }
    this._currentUser = null;
    this._connectionState = 'CLOSED';
    this._unreadMap = new Map();
    this._registeredChannelUrls = [];
  }

  // ── Channel management ────────────────────────────────────────────────────

  private _parseChannelIds(): string[] {
    return this._channelIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  private async _seedUnreadMap(channelUrls: string[], merge = false): Promise<void> {
    if (!this._client || channelUrls.length === 0) return;

    try {
      const fetched = await fetchChannelsUnread(this._client, channelUrls);
      if (merge) {
        const next = new Map(this._unreadMap);
        for (const [url, summary] of fetched) {
          next.set(url, summary);
        }
        this._unreadMap = next;
      } else {
        this._unreadMap = fetched;
      }
      this._dispatch('unread-counts-changed', this._buildUnreadPayload());
    } catch (error) {
      console.warn('[SynergyChatNotifier] Failed to seed unread map:', error);
    }
  }

  /**
   * Registers a single multi-channel GroupChannelHandler.
   * The handler uses the "*" sentinel channel URL so that `registerGroupChannelHandler`
   * delivers all events; we perform our own channel-URL filtering inside callbacks.
   */
  private async _registerHandler(channelUrls: string[]): Promise<void> {
    if (!this._client || channelUrls.length === 0) return;

    // Unregister any previous handler
    if (this._channelHandlerId) {
      unregisterGroupChannelHandler(this._client, this._channelHandlerId);
      this._channelHandlerId = null;
    }

    // We need events from multiple channels so we register once per channel
    // and track the first handler ID. Because registerGroupChannelHandler
    // filters by channelUrl internally, we re-use a shared handler ID approach:
    // register for each URL with the same handlerId base, overriding the filter.
    // The simplest approach: register ONE handler against the first channel URL
    // but override the callbacks to check against ALL watched URLs ourselves.
    const handlerId = `scn_handler_${Date.now()}`;
    this._registeredChannelUrls = [...channelUrls];

    // We call the underlying SDK directly to add a handler that fires for ALL channels.
    // This avoids the single-channel URL filtering in `registerGroupChannelHandler`.
    const id = await this._registerBroadcastHandler(handlerId);
    this._channelHandlerId = id;
  }

  /**
   * Registers a GroupChannelHandler that receives events from any channel,
   * then filters in our callbacks to only watched channel URLs.
   */
  private async _registerBroadcastHandler(handlerId: string): Promise<string | null> {
    const client = this._client;
    if (!client?.groupChannel?.addGroupChannelHandler) return null;

    const { GroupChannelHandler } = await import('@sendbird/chat/groupChannel');
    const handler = new GroupChannelHandler();

    // Cast channel params to `any` to bridge the gap between the SDK's BaseChannel
    // type and our internal SendbirdGroupChannel interface.
    (handler as any).onMessageReceived = (incomingChannel: any, rawMessage: unknown) => {
      this._onMessageReceived(incomingChannel as SendbirdGroupChannel, rawMessage);
    };

    (handler as any).onChannelChanged = (incomingChannel: any) => {
      this._onChannelChanged(incomingChannel as SendbirdGroupChannel);
    };

    (handler as any).onReadStatusUpdated = (incomingChannel: any) => {
      this._onReadStatusUpdated(incomingChannel as SendbirdGroupChannel);
    };

    client.groupChannel.addGroupChannelHandler!(handlerId, handler as any);
    return handlerId;
  }

  /**
   * Called when channelIds attribute changes after initial connect.
   * Adds newly watched channels to unreadMap, removes dropped ones, and re-registers the handler.
   */
  private async _reconcileChannels(): Promise<void> {
    const newUrls = this._parseChannelIds();
    const oldUrls = this._registeredChannelUrls;

    const added = newUrls.filter((u) => !oldUrls.includes(u));
    const obsolete = oldUrls.filter((u) => !newUrls.includes(u));

    // Merge unread data for newly added channels into the existing map
    if (added.length > 0 && this._client) {
      await this._seedUnreadMap(added, true);
    }

    // Drop channels that are no longer watched
    if (obsolete.length > 0) {
      const next = new Map(this._unreadMap);
      for (const url of obsolete) {
        next.delete(url);
      }
      this._unreadMap = next;
      this._dispatch('unread-counts-changed', this._buildUnreadPayload());
    }

    // Re-register the broadcast handler with the full new list
    if (this._client) {
      await this._registerHandler(newUrls);
    }
  }

  // ── Sendbird event handlers ───────────────────────────────────────────────

  private _onMessageReceived(channel: SendbirdGroupChannel, rawMessage: unknown): void {
    const channelUrl = channel?.url;
    if (!channelUrl || !this._registeredChannelUrls.includes(channelUrl)) return;

    const msg = rawMessage as any;
    const senderRaw = msg?.sender ?? msg?.user ?? null;
    const senderName: string =
      typeof senderRaw === 'string'
        ? senderRaw
        : (senderRaw?.nickname ?? senderRaw?.userId ?? '');
    const senderId: string =
      typeof senderRaw === 'string' ? senderRaw : (senderRaw?.userId ?? '');
    const messageText: string = msg?.message ?? msg?.text ?? '';
    const rawPreview =
      messageText.length > 80 ? messageText.slice(0, 77) + '\u2026' : messageText;

    // Update unread map — increment count and update preview
    const existing = this._unreadMap.get(channelUrl);
    const next = new Map(this._unreadMap);
    next.set(channelUrl, {
      channelUrl,
      channelName: existing?.channelName ?? channel.name ?? channelUrl,
      unreadCount: channel.unreadMessageCount ?? (existing?.unreadCount ?? 0) + 1,
      lastSenderName: senderName,
      lastMessagePreview: rawPreview
    });
    this._unreadMap = next;
    this._dispatch('unread-counts-changed', this._buildUnreadPayload());

    const totalUnreadCount = this._totalUnread();
    const channelUnreadCount = next.get(channelUrl)!.unreadCount;

    this._dispatch('notify-message', {
      channelUrl,
      channelName: next.get(channelUrl)!.channelName,
      senderId,
      senderName,
      messageText,
      channelUnreadCount,
      totalUnreadCount
    });
  }

  private _onChannelChanged(channel: SendbirdGroupChannel): void {
    const channelUrl = channel?.url;
    if (!channelUrl || !this._registeredChannelUrls.includes(channelUrl)) return;

    // Refresh from the updated channel object (e.g. read receipts from another session)
    const existing = this._unreadMap.get(channelUrl);
    const lastMessage = channel.lastMessage;

    let lastSenderName = existing?.lastSenderName ?? '';
    let lastMessagePreview = existing?.lastMessagePreview ?? '';

    if (lastMessage) {
      const sender = (lastMessage as any).sender ?? (lastMessage as any).user ?? null;
      lastSenderName =
        typeof sender === 'string'
          ? sender
          : (sender?.nickname ?? sender?.userId ?? lastSenderName);
      const rawText: string = (lastMessage as any).text ?? (lastMessage as any).message ?? '';
      lastMessagePreview = rawText.length > 80 ? rawText.slice(0, 77) + '\u2026' : rawText;
    }

    const next = new Map(this._unreadMap);
    next.set(channelUrl, {
      channelUrl,
      channelName: channel.name ?? existing?.channelName ?? channelUrl,
      unreadCount: channel.unreadMessageCount ?? existing?.unreadCount ?? 0,
      lastSenderName,
      lastMessagePreview
    });
    this._unreadMap = next;
    this._dispatch('unread-counts-changed', this._buildUnreadPayload());
  }

  private _onReadStatusUpdated(channel: SendbirdGroupChannel): void {
    const channelUrl = channel?.url;
    if (!channelUrl || !this._registeredChannelUrls.includes(channelUrl)) return;

    const existing = this._unreadMap.get(channelUrl);
    if (!existing) return;

    const next = new Map(this._unreadMap);
    next.set(channelUrl, {
      ...existing,
      unreadCount: channel.unreadMessageCount ?? 0
    });
    this._unreadMap = next;
    this._dispatch('unread-counts-changed', this._buildUnreadPayload());
  }

  // ── UI event handlers ─────────────────────────────────────────────────────

  private _handleBellClick(): void {
    this._dispatch('bell-clicked', this._buildUnreadPayload());
  }

  // ── Unread payload builder ────────────────────────────────────────────────

  private _buildUnreadPayload() {
    const channels = Array.from(this._unreadMap.values()).map((s) => ({
      channelUrl: s.channelUrl,
      channelName: s.channelName,
      unreadCount: s.unreadCount,
      lastSenderName: s.lastSenderName ?? '',
      lastMessagePreview: s.lastMessagePreview ?? ''
    }));
    return { totalUnreadCount: this._totalUnread(), channels };
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private _totalUnread(): number {
    let total = 0;
    for (const summary of this._unreadMap.values()) {
      total += summary.unreadCount;
    }
    return total;
  }

  private _dispatch<T extends Record<string, unknown>>(eventName: string, detail: T): void {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: true,
        composed: true
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'synergy-chat-notifier': SynergyChatNotifier;
  }
}
